import { useState } from 'react';

import { generatorById, type GenParam } from '@ai-studio/shared';

import {
  addStage,
  addableStages,
  chainLabel,
  generatorOptionLabel,
  moveStage,
  removeStage,
  replaceStage,
  setStageParam,
  validatePipeline,
  type Pipeline,
} from '../../lib/generation.js';
import { NumberField, PropRow, SelectField, Toggle } from '../ui/controls.js';
import { SizeControl } from './SizeControl.js';
import { GeneratorTag } from '../ui/GeneratorTag.js';
import { Icon } from '../ui/icons.js';
import './generation.css';

/** One generator setting rendered from its GenParam spec. */
export function ParamControl({ param, value, onChange }: { param: GenParam; value: unknown; onChange: (value: number | string | boolean) => void }) {
  const current = value ?? param.default;
  let control;
  if (param.type === 'number') {
    control = (
      <NumberField
        value={typeof current === 'number' ? current : Number(current)}
        onChange={onChange}
        min={param.min}
        max={param.max}
        step={param.step ?? 1}
        integer={(param.step ?? 1) >= 1 && Number.isInteger(param.step ?? 1)}
        title={param.hint}
      />
    );
  } else if (param.type === 'select') {
    control = <SelectField value={String(current)} options={param.options ?? []} onChange={onChange} />;
  } else if (param.type === 'bool') {
    control = <Toggle checked={current === true} onChange={onChange} />;
  } else {
    control = (
      <input
        className="textfield"
        value={String(current ?? '')}
        onChange={(e) => onChange(e.target.value)}
        aria-label={param.label}
      />
    );
  }
  return (
    <PropRow label={param.label} hint={param.hint}>
      {control}
    </PropRow>
  );
}

/**
 * Ordered list of generator stages (a model enhancement chain or a pixel
 * pipeline): add, remove, move, replace; each stage shows its speed and
 * quality tags, and (pixel) its settings. Validates the order.
 */
export function PipelineEditor({
  mode,
  pipeline,
  onChange,
  showParams = false,
  disabled = false,
  refine = false,
}: {
  mode: 'model' | 'pixel';
  pipeline: Pipeline;
  onChange: (pipeline: Pipeline) => void;
  /** Render each stage's settings (pixel pipelines). */
  showParams?: boolean;
  disabled?: boolean;
  /** The stages build on a kept result (pixel: only process / upscale stages). */
  refine?: boolean;
}) {
  const [open, setOpen] = useState<number | null>(null);
  const error = pipeline.stages.length ? validatePipeline(mode, pipeline.stages, refine) : null;
  const addable = addableStages(mode, pipeline.stages, refine);
  const [pick, setPick] = useState('');
  const pickValue = addable.some((g) => g.id === pick) ? pick : (addable[0]?.id ?? '');

  return (
    <div className="gen-pipeline">
      {pipeline.stages.length > 0 && (
        <div className="gen-chain-line" title="Each stage's output is rendered and fed into the next stage">
          {chainLabel(pipeline.stages)}
        </div>
      )}
      <ol className="gen-stages">
        {pipeline.stages.map((id, index) => {
          const gen = generatorById(id);
          const params = gen?.params ?? [];
          const expanded = showParams && params.length > 0 && open === index;
          return (
            <li key={`${id}-${index}`} className="gen-stage">
              <div className="gen-stage-row">
                <span className="gen-stage-n">{index + 1}</span>
                <div className="gen-stage-main" title={gen?.specialty}>
                  <span className="gen-stage-name">{gen?.label ?? id}</span>
                  {gen && <GeneratorTag gen={gen} time />}
                </div>
                {showParams && params.length > 0 && (
                  <button type="button" className="ibtn" title={expanded ? 'Hide settings' : 'Stage settings'} aria-expanded={expanded} onClick={() => setOpen(expanded ? null : index)}>
                    <Icon name="settings" size={13} />
                  </button>
                )}
                <button type="button" className="ibtn" title="Move up" disabled={disabled || index === 0} onClick={() => onChange(moveStage(pipeline, index, -1))}>
                  <Icon name="arrowUp" size={13} />
                </button>
                <button type="button" className="ibtn" title="Move down" disabled={disabled || index === pipeline.stages.length - 1} onClick={() => onChange(moveStage(pipeline, index, 1))}>
                  <Icon name="arrowDown" size={13} />
                </button>
                <button type="button" className="ibtn" title="Remove stage" disabled={disabled} onClick={() => onChange(removeStage(pipeline, index))}>
                  <Icon name="x" size={13} />
                </button>
              </div>
              {expanded && (
                <div className="gen-stage-params">
                  {params.map((p) =>
                    p.type === 'size' ? (
                      <SizeControl
                        key={p.key}
                        param={p}
                        width={Number(pipeline.params[index]?.[p.key] ?? p.default)}
                        height={Number(pipeline.params[index]?.[p.heightKey ?? 'height'] ?? p.heightDefault ?? p.default)}
                        onChange={(w, h) => onChange(setStageParam(setStageParam(pipeline, index, p.key, w), index, p.heightKey ?? 'height', h))}
                      />
                    ) : (
                      <ParamControl key={p.key} param={p} value={pipeline.params[index]?.[p.key]} onChange={(v) => onChange(setStageParam(pipeline, index, p.key, v))} />
                    ),
                  )}
                  <button type="button" className="link-btn" onClick={() => onChange(replaceStage(pipeline, index, id))}>
                    Reset settings
                  </button>
                </div>
              )}
            </li>
          );
        })}
      </ol>
      {error && (
        <p className="gen-error" role="alert">
          <Icon name="warning" size={12} /> {error}
        </p>
      )}
      <div className="gen-add-row">
        <SelectField
          value={pickValue}
          options={addable.map((g) => ({ value: g.id, label: generatorOptionLabel(g) }))}
          onChange={setPick}
          placeholder="Add a stage"
        />
        <button type="button" className="ghost small" disabled={disabled || !pickValue} onClick={() => onChange(addStage(pipeline, pickValue))}>
          <Icon name="plus" size={12} /> Add stage
        </button>
      </div>
    </div>
  );
}
